import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useNumericInput from "../../hooks/useNumericInput";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import CartItemsinCheckout from "../canvas/CartItemsinCheckout";
import { useCart } from "../../context/CartContext";
import Breadcrump from "../../components/Breadcrump";
import Summary from "./common/Summary";
import addressAPI from "../../api/addressApi";
import { useCheckout } from "../../hooks/useCheckout";
import { showError } from "../../utils/toastUtils";
import PaymentService from "./gateways/PaymentServices";
import { useSiteConfig } from "../../context/SiteConfigContext";

import {
  FiUser,
  FiMapPin,
  FiCreditCard,
  FiArrowRight,
} from "react-icons/fi";

function Checkout() {
  useNumericInput();
  useAlphabeticInput();

  const {
    cartItems,
    fetchCart,
    cartSummary,
  } = useCart();

  const { config } = useSiteConfig();

  const navigate = useNavigate();

  const [errors, setErrors] =
    useState({});

  const [loading, setLoading] =
    useState(false);

  const [active, setActive] =
    useState("contact");

  const [
    savedAddresses,
    setSavedAddresses,
  ] = useState([]);

  const [
    selectedAddress,
    setSelectedAddress,
  ] = useState("");

  const paymentMethods = [
    {
      code: "razorpay",
      name: "Online Payment (Razorpay)",
    },
  ];

  const auth = JSON.parse(
    localStorage.getItem("token") ??
      "{}"
  );

  const INITIAL_FORM_DATA = {
    email:
      auth.email ||
      sessionStorage.getItem(
        "emailId"
      ) ||
      "",

    phone:
      sessionStorage.getItem(
        "phoneNum"
      ) || "",

    firstName: "",
    lastName: "",

    address: {
      addressType: "home",
      addressLine1: "",
      addressLine2: "",
      pinCode: "",
      district: "",
      state: "",
      country: "India",
    },

    paymentMethod: "razorpay",
  };

  const [formData, setFormData] =
    useState(INITIAL_FORM_DATA);

  const { placeOrder } =
    useCheckout(
      cartItems,
      formData
    );

  // =========================
  // HANDLE CHANGE
  // =========================

  const handleChange = (e) => {
    const { name, value } =
      e.target;

    setSelectedAddress("");

    if (name.includes(".")) {
      const [parent, child] =
        name.split(".");

      setFormData((prev) => ({
        ...prev,

        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // =========================
  // VALIDATION
  // =========================

  const emailRegex =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const phoneRegex =
    /^[6-9]\d{9}$/;

  const isContactValid =
    !!formData.email?.trim() &&
    !!formData.phone?.trim();

  const validate = () => {
    const errs = {};

    if (!formData.email)
      errs.email =
        "Email is required";

    else if (
      !emailRegex.test(
        formData.email
      )
    )
      errs.email = "Invalid email";

    if (!formData.phone)
      errs.phone =
        "Phone is required";

    else if (
      !phoneRegex.test(
        formData.phone
      )
    )
      errs.phone =
        "Invalid phone";

    setErrors(errs);

    return (
      Object.keys(errs).length === 0
    );
  };

  // =========================
  // AUTO OPEN ADDRESS STEP
  // =========================

  useEffect(() => {
    if (
      formData.email &&
      formData.phone
    ) {
      if (validate()) {
        getAllMyAddress();

        setActive("address");
      }
    }
  }, []);

  // =========================
  // CONTINUE BUTTON
  // =========================

  const handleContinue = (e) => {
    e.preventDefault();

    if (!validate()) return;

    setActive("address");

    getAllMyAddress();
  };

  // =========================
  // FETCH SAVED ADDRESS
  // =========================

  const getAllMyAddress =
    async () => {
      try {
        const res =
          await addressAPI.fetchAllAddress(
            {
              email:
                formData.email,
            }
          );

        if (res.code === 200)
          setSavedAddresses(
            res.data
          );
      } catch (err) {
        console.error(err);
      }
    };

  // =========================
  // SELECT SAVED ADDRESS
  // =========================

  const handleSavedAddressSelect = (
    address
  ) => {
    setSelectedAddress(
      address._id
    );

    setFormData((prev) => ({
      ...prev,

      firstName:
        address.firstName || "",

      lastName:
        address.lastName || "",

      address: {
        addressType:
          address.addressType ||
          "home",

        addressLine1:
          address.addressLine1 ||
          "",

        addressLine2:
          address.addressLine2 ||
          "",

        pinCode:
          address.pinCode || "",

        district:
          address.district || "",

        state:
          address.state || "",

        country:
          address.country ||
          "India",
      },
    }));
  };

  // =========================
  // PLACE ORDER
  // =========================

  const handlePlaceOrder =
    async (e) => {
      e.preventDefault();

      setLoading(true);

      try {
        const action =
          await placeOrder();

        if (
          action?.type ===
          "PAYMENT_STARTED"
        ) {
          const {
            orderId,
            paymentMethod,
            gatewayOrderId,
            amount,
            currency,
          } = action.payload;

          PaymentService.pay(
            paymentMethod.toLowerCase(),
            {
              gatewayOrderId,
              orderId,
              amount,
              currency,
              formData,
              navigate,
              config,
              fetchCart,
            }
          );
        }
      } catch (err) {
        showError(
          err.message ||
            "Order failed"
        );
      } finally {
        setLoading(false);
      }
    };

  return (
    <div className="checkout-page-funky">
      <Breadcrump title="Checkout" />

      <div className="container py-5">
        <div className="row g-5">

          {/* LEFT SECTION */}

          <div className="col-xl-8">
            <div className="checkout-steps-wrapper">

              {/* CONTACT STEP */}

              <div
                className={`checkout-step-box ${
                  active === "contact"
                    ? "active"
                    : ""
                }`}
              >
                <div
                  className="step-header"
                  onClick={() =>
                    setActive(
                      "contact"
                    )
                  }
                >
                  <div className="step-number">
                    01
                  </div>

                  <h5>
                    <FiUser className="me-2" />
                    CONTACT DETAILS
                  </h5>
                </div>

                {active ===
                  "contact" && (
                  <div className="step-content p-4">
                    <div className="row g-3">

                      <div className="col-md-6">
                        <label className="funky-label">
                          Email
                          Address
                        </label>

                        <input
                          name="email"
                          value={
                            formData.email
                          }
                          onChange={
                            handleChange
                          }
                          className="funky-input"
                          placeholder="example@mail.com"
                        />

                        {errors.email && (
                          <small className="text-danger">
                            {
                              errors.email
                            }
                          </small>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className="funky-label">
                          Phone Number
                        </label>

                        <input
                          name="phone"
                          value={
                            formData.phone
                          }
                          maxLength={
                            10
                          }
                          onChange={
                            handleChange
                          }
                          className="funky-input only-numeric"
                          placeholder="10 Digit Mobile"
                        />

                        {errors.phone && (
                          <small className="text-danger">
                            {
                              errors.phone
                            }
                          </small>
                        )}
                      </div>

                      <div className="col-12">
                        <button
                          className="funky-btn-step mt-3"
                          onClick={
                            handleContinue
                          }
                        >
                          CONTINUE TO
                          ADDRESS{" "}
                          <FiArrowRight />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ADDRESS STEP */}

              <div
                className={`checkout-step-box mt-4 ${
                  active === "address"
                    ? "active"
                    : ""
                }`}
              >
                <div
                  className="step-header"
                  onClick={() =>
                    isContactValid &&
                    setActive(
                      "address"
                    )
                  }
                >
                  <div className="step-number">
                    02
                  </div>

                  <h5>
                    <FiMapPin className="me-2" />
                    SHIPPING ADDRESS
                  </h5>
                </div>

                {active ===
                  "address" && (
                  <div className="step-content p-4">

                    {/* SAVED ADDRESS */}

                    {savedAddresses.length >
                      0 && (
                      <div className="saved-address-grid mb-4">
                        <h6>
                          Select Saved
                          Address:
                        </h6>

                        <div className="row g-3">
                          {savedAddresses.map(
                            (addr) => (
                              <div
                                className="col-md-6"
                                key={
                                  addr._id
                                }
                              >
                                <div
                                  className={`address-card ${
                                    selectedAddress ===
                                    addr._id
                                      ? "selected"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    handleSavedAddressSelect(
                                      addr
                                    )
                                  }
                                >
                                  <strong>
                                    {addr.addressType.toUpperCase()}
                                  </strong>

                                  <p>
                                    {
                                      addr.addressLine1
                                    }
                                    ,{" "}
                                    {
                                      addr.district
                                    }
                                    ,{" "}
                                    {
                                      addr.state
                                    }
                                  </p>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                    {/* FORM */}

                    <div className="row g-3">

                      <div className="col-md-6">
                        <label className="funky-label">
                          First Name
                        </label>

                        <input
                          name="firstName"
                          value={
                            formData.firstName
                          }
                          onChange={
                            handleChange
                          }
                          className="funky-input"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="funky-label">
                          Last Name
                        </label>

                        <input
                          name="lastName"
                          value={
                            formData.lastName
                          }
                          onChange={
                            handleChange
                          }
                          className="funky-input"
                        />
                      </div>

                      <div className="col-12">
                        <label className="funky-label">
                          Full Address
                        </label>

                        <input
                          name="address.addressLine1"
                          value={
                            formData
                              .address
                              .addressLine1
                          }
                          onChange={
                            handleChange
                          }
                          className="funky-input"
                          placeholder="House No, Street, Area"
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="funky-label">
                          Pincode
                        </label>

                        <input
                          name="address.pinCode"
                          maxLength={
                            6
                          }
                          value={
                            formData
                              .address
                              .pinCode
                          }
                          onChange={
                            handleChange
                          }
                          className="funky-input only-numeric"
                          placeholder="6 Digits"
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="funky-label">
                          District /
                          City
                        </label>

                        <input
                          name="address.district"
                          value={
                            formData
                              .address
                              .district ||
                            ""
                          }
                          onChange={
                            handleChange
                          }
                          className="funky-input"
                          placeholder="Enter District / City"
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="funky-label">
                          State
                        </label>

                        <input
                          name="address.state"
                          value={
                            formData
                              .address
                              .state ||
                            ""
                          }
                          onChange={
                            handleChange
                          }
                          className="funky-input"
                          placeholder="Enter State"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SECTION */}

          <div className="col-xl-4">
            <div className="checkout-summary-sidebar">
              <div className="summary-card-funky">

                <h4 className="summary-title-funky">
                  YOUR ORDER
                </h4>

                <div className="checkout-items-list mb-4">
                  {cartItems.map(
                    (item) => (
                      <CartItemsinCheckout
                        item={item}
                        key={item._id}
                      />
                    )
                  )}
                </div>

                <Summary
                  cartItems={
                    cartItems
                  }
                  cartSummary={
                    cartSummary
                  }
                />

                <hr className="my-4 border-2" />

                <h5 className="mb-3">
                  <FiCreditCard className="me-2" />
                  PAYMENT METHOD
                </h5>

                <div className="payment-options">
                  {paymentMethods.map(
                    (m) => (
                      <div
                        className="payment-method-item"
                        key={
                          m.code
                        }
                      >
                        <input
                          type="radio"
                          checked
                          id={m.code}
                          readOnly
                        />

                        <label
                          htmlFor={
                            m.code
                          }
                        >
                          {m.name}
                        </label>
                      </div>
                    )
                  )}
                </div>

                <button
                  className="place-order-btn-funky mt-4"
                  onClick={
                    handlePlaceOrder
                  }
                  disabled={loading}
                >
                  {loading
                    ? "PROCESSING..."
                    : "CONFIRM & PAY NOW ⚡"}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Checkout;